<template>
    <div class="lp-section-form" :data-section-type="s.type">
        <!-- ============ hero ============ -->
        <template v-if="s.type === 'hero'">
            <div class="row">
                <div class="col-md-6 form-group" :class="cls('image_key')">
                    <label class="form-control-label">{{ t("f.image") }} <small class="text-muted">{{ t("hint.image") }}</small></label>
                    <s3-image-upload object-type="storefront" :presign="presign" :disabled="ro" :value="s.image_url || ''" @input="setUrl('image', $event)" @update:objectKey="setKey('image', $event)"></s3-image-upload>
                    <small v-if="err('image_key')" class="text-danger">{{ rule(err("image_key")) }}</small>
                </div>
                <div class="col-md-6 form-group">
                    <label class="form-control-label">{{ t("f.image_mobile") }} <small class="text-muted">{{ t("hint.image_mobile") }}</small></label>
                    <s3-image-upload object-type="storefront" :presign="presign" :disabled="ro" :value="s.image_mobile_url || ''" @input="setUrl('image_mobile', $event)" @update:objectKey="setKey('image_mobile', $event)"></s3-image-upload>
                </div>
                <div class="col-md-6 form-group">
                    <label class="form-control-label">{{ t("f.video") }} <small class="text-muted">{{ t("hint.video") }}</small></label>
                    <s3-image-upload object-type="storefront" :presign="presign" :disabled="ro" accept="video/mp4" :max-size="15 * 1024 * 1024" :hint="t('hint.video_drop')" :value="s.video_url || ''" @input="setUrl('video', $event)" @update:objectKey="setKey('video', $event)"></s3-image-upload>
                </div>
                <div v-if="s.video_key" class="col-md-6 form-group" :class="cls('video_poster_key')">
                    <label class="form-control-label">{{ t("f.video_poster") }}</label>
                    <s3-image-upload object-type="storefront" :presign="presign" :disabled="ro" :value="s.video_poster_url || ''" @input="setUrl('video_poster', $event)" @update:objectKey="setKey('video_poster', $event)"></s3-image-upload>
                    <small v-if="err('video_poster_key')" class="text-danger">{{ rule(err("video_poster_key")) }}</small>
                </div>
            </div>
            <text-field :s="s" f="title" :max="80" :label="t('f.title')" :ro="ro" :errors="errors"></text-field>
            <text-field :s="s" f="subtitle" :max="160" :label="t('f.subtitle')" :ro="ro" :errors="errors"></text-field>
            <text-field :s="s" f="cta_label" :max="30" :label="t('f.cta_label')" :ro="ro" :errors="errors"></text-field>
            <div class="form-group">
                <label class="form-control-label">{{ t("f.cta_action") }}</label>
                <lp-cta-action :value="s.cta_action" :sections="sections" :own-id="s.id" :errors="errors" :disabled="ro" :label-of="labelOf"></lp-cta-action>
            </div>
            <div class="row">
                <div class="col-md-6 form-group">
                    <label class="form-control-label">{{ t("f.text_theme") }}</label>
                    <el-radio-group v-model="s.text_theme" size="small" :disabled="ro">
                        <el-radio-button label="light">{{ t("f.theme_light") }}</el-radio-button>
                        <el-radio-button label="dark">{{ t("f.theme_dark") }}</el-radio-button>
                    </el-radio-group>
                </div>
                <div class="col-md-6 form-group">
                    <label class="form-control-label">{{ t("f.overlay") }}</label>
                    <el-slider v-model="s.overlay" :min="0" :max="60" :disabled="ro" show-input input-size="mini"></el-slider>
                </div>
            </div>
        </template>

        <!-- ============ product grid ============ -->
        <template v-else-if="s.type === 'product_grid'">
            <text-field :s="s" f="title" :label="t('f.title')" :ro="ro" :errors="errors"></text-field>
            <div class="form-group" :class="cls('product_ids')">
                <label class="form-control-label">{{ t("f.products") }} ({{ (s.product_ids || []).length }}/24)</label>
                <lp-product-picker v-model="s.product_ids" multiple :max="24" :unavailable="warn.products" :disabled="ro"></lp-product-picker>
                <small v-if="err('product_ids')" class="text-danger">{{ rule(err("product_ids")) }}</small>
            </div>
        </template>

        <!-- ============ featured product ============ -->
        <template v-else-if="s.type === 'featured_product'">
            <div class="form-group" :class="cls('product_id')">
                <label class="form-control-label">{{ t("f.product") }}</label>
                <lp-product-picker v-model="s.product_id" :unavailable="warn.products" :disabled="ro"></lp-product-picker>
                <small v-if="err('product_id')" class="text-danger">{{ rule(err("product_id")) }}</small>
            </div>
            <text-field :s="s" f="title_override" :max="80" :label="t('f.title_override')" :ro="ro" :errors="errors"></text-field>
            <text-field :s="s" f="badge_text" :max="20" :label="t('f.badge_text')" :ro="ro" :errors="errors"></text-field>
            <el-checkbox v-model="s.show_description" :disabled="ro">{{ t("f.show_description") }}</el-checkbox>
        </template>

        <!-- ============ promotion ============ -->
        <template v-else-if="s.type === 'promotion'">
            <text-field :s="s" f="title" :label="t('f.title')" :ro="ro" :errors="errors"></text-field>
            <div class="form-group" :class="cls('promotion_ids')">
                <label class="form-control-label">{{ t("f.promotions") }} ({{ (s.promotion_ids || []).length }}/6)</label>
                <el-select v-model="s.promotion_ids" class="d-block" multiple filterable :multiple-limit="6" :disabled="ro" :placeholder="t('f.search_promotion')" @visible-change="loadPromotions">
                    <el-option v-for="p in promotions" :key="p.id" :value="p.id" :label="promoLabel(p)">
                        <span>{{ promoLabel(p) }}</span>
                        <el-tag v-if="warn.promotions.indexOf(p.id) !== -1" type="danger" size="mini" class="ml-2">{{ t("err.unavailable_product") }}</el-tag>
                    </el-option>
                </el-select>
                <div v-if="warn.promotions.length" class="mt-1">
                    <el-tag v-for="id in warn.promotions" :key="id" type="danger" size="mini" effect="dark" class="mr-1" data-testid="lp-unavailable">#{{ id }} {{ t("err.unavailable_product") }}</el-tag>
                </div>
                <small v-if="err('promotion_ids')" class="text-danger">{{ rule(err("promotion_ids")) }}</small>
            </div>
        </template>

        <!-- ============ countdown ============ -->
        <template v-else-if="s.type === 'countdown'">
            <text-field :s="s" f="title" :max="80" :label="t('f.title')" :ro="ro" :errors="errors"></text-field>
            <div class="form-group" :class="cls('ends_at')">
                <label class="form-control-label">{{ t("f.ends_at") }} <small class="text-muted">Asia/Bangkok</small></label>
                <el-date-picker :value="bkk(s.ends_at)" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" format="yyyy-MM-dd HH:mm" class="w-100" :disabled="ro" @input="s.ends_at = toIso($event) || ''"></el-date-picker>
                <small v-if="err('ends_at')" class="text-danger">{{ rule(err("ends_at")) }}</small>
            </div>
            <text-field :s="s" f="ended_text" :max="80" :label="t('f.ended_text')" :ro="ro" :errors="errors"></text-field>
            <div class="d-flex flex-wrap align-items-center" style="gap: 16px;">
                <el-checkbox v-model="s.hide_when_ended" :disabled="ro">{{ t("f.hide_when_ended") }}</el-checkbox>
                <el-radio-group v-model="s.theme" size="small" :disabled="ro">
                    <el-radio-button label="dark">{{ t("f.theme_dark") }}</el-radio-button>
                    <el-radio-button label="light">{{ t("f.theme_light") }}</el-radio-button>
                </el-radio-group>
            </div>
        </template>

        <!-- ============ coupon ============ -->
        <template v-else-if="s.type === 'coupon'">
            <text-field :s="s" f="title" :label="t('f.title')" :ro="ro" :errors="errors"></text-field>
            <text-field :s="s" f="description" :max="200" :label="t('f.description')" :ro="ro" :errors="errors" textarea></text-field>
            <text-field :s="s" f="code" :max="40" :label="t('f.code')" :ro="ro" :errors="errors"></text-field>
            <text-field :s="s" f="button_label" :max="30" :label="t('f.button_label')" :ro="ro" :errors="errors"></text-field>
            <div class="form-group">
                <label class="form-control-label">{{ t("f.image") }}</label>
                <s3-image-upload object-type="storefront" :presign="presign" :disabled="ro" :value="s.image_url || ''" @input="setUrl('image', $event)" @update:objectKey="setKey('image', $event)"></s3-image-upload>
            </div>
        </template>

        <!-- ============ rich text ============ -->
        <template v-else-if="s.type === 'rich_text'">
            <div class="form-group" :class="cls('html')">
                <label class="form-control-label">{{ t("f.html") }} <small class="text-muted">{{ (s.html || "").length }}/20000</small></label>
                <!-- Never hand Quill unsanitised HTML (stored XSS in the BO session). -->
                <!-- Toolbar + formats = the API allow-list only (what you see is what is saved).
                     Images upload through the LP presign route instead of base64 (which the API strips). -->
                <vue-editor
                    :value="safeHtml"
                    :disabled="ro"
                    :editor-toolbar="editorToolbar"
                    :editor-options="editorOptions"
                    use-custom-image-handler
                    @image-added="onEditorImage"
                    @focus="htmlTouched = true"
                    @input="onHtml"
                ></vue-editor>
                <small v-if="imageError" class="text-danger">{{ imageError }}</small>
                <small v-if="err('html')" class="text-danger">{{ rule(err("html")) }}</small>
            </div>
        </template>

        <!-- ============ faq ============ -->
        <template v-else-if="s.type === 'faq'">
            <text-field :s="s" f="title" :label="t('f.title')" :ro="ro" :errors="errors"></text-field>
            <div v-for="(it, i) in s.items" :key="i" class="border rounded p-2 mb-2">
                <div class="d-flex align-items-center mb-1">
                    <strong class="small flex-fill">{{ t("f.faq_q") }} {{ i + 1 }}</strong>
                    <template v-if="!ro">
                        <base-button size="sm" type="neutral" class="p-1" :disabled="i === 0" @click="moveItem(i, -1)"><i class="fa fa-arrow-up"></i></base-button>
                        <base-button size="sm" type="neutral" class="p-1" :disabled="i === s.items.length - 1" @click="moveItem(i, 1)"><i class="fa fa-arrow-down"></i></base-button>
                        <base-button size="sm" type="danger" class="p-1" @click="s.items.splice(i, 1)"><i class="fa fa-times"></i></base-button>
                    </template>
                </div>
                <el-input v-model="it.q" maxlength="200" :disabled="ro" :placeholder="t('f.faq_q')" class="mb-1"></el-input>
                <el-input v-model="it.a" type="textarea" :rows="2" maxlength="1000" :disabled="ro" :placeholder="t('f.faq_a')"></el-input>
            </div>
            <small v-if="err('items')" class="text-danger d-block mb-1">{{ rule(err("items")) }}</small>
            <base-button v-if="!ro" size="sm" type="secondary" :disabled="s.items.length >= 20" @click="s.items.push({ q: '', a: '' })"><i class="fa fa-plus"></i> {{ t("f.add_faq") }}</base-button>
        </template>

        <!-- ============ line chat ============ -->
        <template v-else-if="s.type === 'line_chat'">
            <text-field :s="s" f="label" :max="30" :label="t('f.label')" :ro="ro" :errors="errors"></text-field>
            <text-field :s="s" f="url" :label="t('f.url')" :ro="ro" :errors="errors" placeholder="https://line.me/R/ti/p/@fin9"></text-field>
            <text-field :s="s" f="note" :max="120" :label="t('f.note')" :ro="ro" :errors="errors"></text-field>
        </template>

        <!-- ============ sticky cta ============ -->
        <template v-else-if="s.type === 'sticky_cta'">
            <text-field :s="s" f="label" :max="30" :label="t('f.label')" :ro="ro" :errors="errors"></text-field>
            <text-field :s="s" f="sublabel" :max="40" :label="t('f.sublabel')" :ro="ro" :errors="errors"></text-field>
            <div class="form-group">
                <label class="form-control-label">{{ t("f.cta_action") }}</label>
                <lp-cta-action :value="s.cta_action" :sections="sections" :own-id="s.id" :errors="errors" :disabled="ro" :label-of="labelOf"></lp-cta-action>
            </div>
        </template>
    </div>
</template>

<script>
import { Select, Option, RadioGroup, Radio, RadioButton, Slider, Checkbox, DatePicker, Tag } from "element-ui";
import { VueEditor } from "vue2-editor";
import LpProductPicker from "./LpProductPicker.vue";
import LpCtaAction from "./LpCtaAction.vue";
import { LandingPageService } from "@/resource";
import S3ImageUpload from "@/components/Inputs/S3ImageUpload.vue";
import { LP_EDITOR_FORMATS, LP_EDITOR_TOOLBAR, bkkLocalToIso, isoToBkkLocal, sanitizeLpHtml } from "@/utils/marketing";

// One text input bound to s[f], with a counter and the field's error.
const TextField = {
    name: "TextField",
    props: { s: Object, f: String, label: String, max: Number, ro: Boolean, errors: Object, textarea: Boolean, placeholder: String },
    computed: {
        rule() {
            return this.errors && this.errors[this.f];
        }
    },
    render(h) {
        const input = h("el-input", {
            props: { value: this.s[this.f], maxlength: this.max || undefined, showWordLimit: !!this.max, disabled: this.ro, type: this.textarea ? "textarea" : "text", placeholder: this.placeholder },
            attrs: { "data-field": this.f },
            on: { input: (v) => this.$set(this.s, this.f, v) }
        });
        const kids = [h("label", { class: "form-control-label" }, this.label), input];
        if (this.rule) {
            const k = "marketing.lp.rule." + this.rule;
            kids.push(h("small", { class: "text-danger" }, this.$te(k) ? this.$t(k) : this.rule));
        }
        return h("div", { class: ["form-group", { "mk-invalid": !!this.rule }] }, kids);
    }
};

// Per-type form for one LP section (§2.3 field order). Edits `section` in place
// (the editor owns the array); images go through the LP presign route.
export default {
    name: "LpSectionForm",
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [RadioGroup.name]: RadioGroup,
        [Radio.name]: Radio,
        [RadioButton.name]: RadioButton,
        [Slider.name]: Slider,
        [Checkbox.name]: Checkbox,
        [DatePicker.name]: DatePicker,
        [Tag.name]: Tag,
        S3ImageUpload, VueEditor, LpProductPicker, LpCtaAction, TextField },
    props: {
        section: { type: Object, required: true },
        sections: { type: Array, default: () => [] },
        errors: { type: Object, default: () => ({}) },
        warn: { type: Object, default: () => ({ products: [], promotions: [] }) },
        ro: { type: Boolean, default: false },
        labelOf: { type: Function, required: true }
    },
    data() {
        return {
            promotions: [],
            promotionsLoaded: false,
            htmlTouched: false,
            imageError: "",
            editorToolbar: LP_EDITOR_TOOLBAR,
            editorOptions: { formats: LP_EDITOR_FORMATS }
        };
    },
    computed: {
        s() {
            return this.section;
        },
        safeHtml() {
            return sanitizeLpHtml(this.s.html);
        }
    },
    created() {
        if (this.s.type === "promotion") this.loadPromotions(true);
    },
    methods: {
        t(k) {
            return this.$t("marketing.lp." + k);
        },
        err(f) {
            return this.errors[f];
        },
        cls(f) {
            return { "mk-invalid": !!this.errors[f] };
        },
        rule(r) {
            const k = "marketing.lp.rule." + r;
            return this.$te(k) ? this.$t(k) : r;
        },
        presign(req) {
            return LandingPageService.presign(req);
        },
        setUrl(base, url) {
            this.$set(this.s, base + "_url", url);
        },
        setKey(base, key) {
            this.$set(this.s, base + "_key", key);
        },
        // Before the editor ever had focus, an input event is Quill normalising
        // the loaded HTML (e.g. wrapping an <img> in <p>), not an edit: the
        // parent re-baselines instead of flagging unsaved changes.
        onHtml(v) {
            const html = sanitizeLpHtml(v);
            if (this.htmlTouched) this.$set(this.s, "html", html);
            else this.$emit("normalize", () => this.$set(this.s, "html", html));
        },
        // Toolbar image → presign + PUT → insert the CDN URL (http/https, which
        // the allow-list keeps). Images ≤ 5 MB, jpg/png/webp.
        async onEditorImage(file, Editor, cursorLocation, resetUploader) {
            this.imageError = "";
            try {
                if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 5 * 1024 * 1024) throw new Error("type");
                const res = await LandingPageService.presign({ object_type: "storefront", file_name: file.name, content_type: file.type });
                const d = (res && res.data) || {};
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("PUT", d.upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(String(xhr.status))));
                    xhr.onerror = () => reject(new Error("network"));
                    xhr.send(file);
                });
                this.htmlTouched = true;
                Editor.insertEmbed(cursorLocation, "image", d.cdn_url);
            } catch (e) {
                this.imageError = this.$t("errors.upload.presign_failed") || "Upload failed";
            } finally {
                resetUploader();
            }
        },
        bkk: isoToBkkLocal,
        toIso: bkkLocalToIso,
        moveItem(i, dir) {
            const j = i + dir;
            const arr = this.s.items;
            if (j < 0 || j >= arr.length) return;
            const tmp = arr[i];
            this.$set(arr, i, arr[j]);
            this.$set(arr, j, tmp);
        },
        promoLabel(p) {
            const name = p.name_th || p.name || p.name_en || "";
            return `#${p.id} ${name}`;
        },
        async loadPromotions(open) {
            if (!open || this.promotionsLoaded) return;
            this.promotionsLoaded = true;
            try {
                const res = await LandingPageService.listPromotions({ page: 1, limit: 100 });
                const body = res.data || {};
                this.promotions = body.data || body.items || [];
            } catch (e) {
                this.promotionsLoaded = false;
            }
        }
    }
};
</script>
